import React, { useState, useEffect, useRef } from 'react';

const PerformanceDashboard = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [metrics, setMetrics] = useState({
    renderTime: 0,
    memoryUsage: 0,
    totalMemory: 0,
    componentCounts: {},
    pageLoadTime: 0,
    bundleSize: 0
  });
  const [vitals, setVitals] = useState({});
  const startTime = useRef(performance.now()); // Used for render time calculations

  useEffect(() => {
    // Calculate page load time
    if (performance.timing) {
      const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
      setMetrics(prev => ({ ...prev, pageLoadTime: loadTime }));
    }

    // Monitor memory usage
    const memoryInterval = setInterval(() => {
      if (performance.memory) {
        const { usedJSHeapSize, totalJSHeapSize } = performance.memory;
        setMetrics(prev => ({
          ...prev,
          memoryUsage: (usedJSHeapSize / 1024 / 1024).toFixed(2),
          totalMemory: (totalJSHeapSize / 1024 / 1024).toFixed(2)
        }));
      }
    }, 2000);

    // Calculate initial render time
    const currentTime = performance.now();
    const initialRenderTime = currentTime - startTime.current;
    setMetrics(prev => ({
      ...prev,
      renderTime: initialRenderTime.toFixed(2)
    }));

    // Monitor render performance
    const renderObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.entryType === 'measure') {
          setMetrics(prev => ({
            ...prev,
            renderTime: entry.duration.toFixed(2)
          }));
        }
      });
    });

    if (window.PerformanceObserver) {
      renderObserver.observe({ entryTypes: ['measure'] });
    }

    // Load Web Vitals if available
    if (process.env.NODE_ENV === 'development') {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS((metric) => setVitals(prev => ({ ...prev, cls: metric.value.toFixed(4) })));
        getFID((metric) => setVitals(prev => ({ ...prev, fid: metric.value.toFixed(2) })));
        getFCP((metric) => setVitals(prev => ({ ...prev, fcp: metric.value.toFixed(2) })));
        getLCP((metric) => setVitals(prev => ({ ...prev, lcp: metric.value.toFixed(2) })));
        getTTFB((metric) => setVitals(prev => ({ ...prev, ttfb: metric.value.toFixed(2) })));
      }).catch(() => {
        console.log('Web Vitals not available');
      });
    }

    return () => {
      clearInterval(memoryInterval);
      if (renderObserver) renderObserver.disconnect();
    };
  }, []);

  // Only show in development mode
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="fixed bottom-4 right-4 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all duration-200 font-mono text-sm"
        title="Toggle Performance Dashboard"
      >
        📊
      </button>

      {/* Performance Dashboard */}
      {isVisible && (
        <div className="fixed bottom-16 right-4 z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-2xl p-4 w-80 max-h-96 overflow-y-auto font-mono text-xs">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-gray-800 dark:text-gray-200">
              🚀 Performance Dashboard
            </h3>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          {/* Core Metrics */}
          <div className="space-y-2 mb-4">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600 pb-1">
              📈 Core Metrics
            </h4>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                <div className="text-blue-800 dark:text-blue-200 font-medium">Memory</div>
                <div className="text-blue-600 dark:text-blue-300">
                  {metrics.memoryUsage}MB / {metrics.totalMemory}MB
                </div>
              </div>
              
              <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded">
                <div className="text-green-800 dark:text-green-200 font-medium">Page Load</div>
                <div className="text-green-600 dark:text-green-300">
                  {(metrics.pageLoadTime / 1000).toFixed(2)}s
                </div>
              </div>
            </div>
          </div>

          {/* Web Vitals */}
          {Object.keys(vitals).length > 0 && (
            <div className="space-y-2 mb-4">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600 pb-1">
                🎯 Web Vitals
              </h4>
              
              <div className="grid grid-cols-2 gap-1 text-xs">
                {vitals.lcp && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">LCP:</span>
                    <span className={`font-medium ${parseFloat(vitals.lcp) < 2500 ? 'text-green-600' : parseFloat(vitals.lcp) < 4000 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {vitals.lcp}ms
                    </span>
                  </div>
                )}
                {vitals.fid && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">FID:</span>
                    <span className={`font-medium ${parseFloat(vitals.fid) < 100 ? 'text-green-600' : parseFloat(vitals.fid) < 300 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {vitals.fid}ms
                    </span>
                  </div>
                )}
                {vitals.cls && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">CLS:</span>
                    <span className={`font-medium ${parseFloat(vitals.cls) < 0.1 ? 'text-green-600' : parseFloat(vitals.cls) < 0.25 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {vitals.cls}
                    </span>
                  </div>
                )}
                {vitals.fcp && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">FCP:</span>
                    <span className={`font-medium ${parseFloat(vitals.fcp) < 1800 ? 'text-green-600' : parseFloat(vitals.fcp) < 3000 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {vitals.fcp}ms
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Performance Tips */}
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600 pb-1">
              💡 Performance Tips
            </h4>
            
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <div className="flex items-center gap-1">
                <span className="text-green-500">✓</span>
                <span>Code splitting enabled</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-green-500">✓</span>
                <span>Service Worker active</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-green-500">✓</span>
                <span>Components memoized</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-blue-500">ℹ</span>
                <span>PWA ready for install</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (navigator.serviceWorker) {
                    navigator.serviceWorker.getRegistrations().then(registrations => {
                      console.log('Service Workers:', registrations.length);
                    });
                  }
                }}
                className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-2 py-1 rounded text-xs transition-colors"
              >
                Check SW
              </button>
              <button
                onClick={() => {
                  console.clear();
                  console.log('Performance dashboard cleared console');
                }}
                className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-2 py-1 rounded text-xs transition-colors"
              >
                Clear Console
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PerformanceDashboard;